// ===================================================================================================
// InstantQuotesSearchConfigCrud.jsx
// ---------------------------------------------------------------------------------------------------
// - Per-tab state isolation: each browser tab keeps its own operation and selected line.
// - Reads 'operation' and 'InstantQuoteSearchLine' from the global store only on mount;
//   then copies them into this tab's local snapshot and removes the global seeds.
// - Persists and restores a full per-tab snapshot (including operation & sessionLine).
// - Sends markup as the raw number the user typed (ex "15" => 15, "10%" => 10).
// - Keeps PlaceAutocomplete logic; ServiceScope continues to drive PlaceType filtering.
// ---------------------------------------------------------------------------------------------------

import React, { useState, useEffect, useContext, useMemo, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  LoadingContext,
  MButton,
  M_StateActions,
  MHeader,
  BufferService,
  NavigateContext,
} from 'src/M_Components';
import PropTypes from 'prop-types';
import { isFailurePayload, apiMessage } from 'src/components/utils/apiResponse';

// Children
import InstantQuotesSearchMainForm from '../../components/InstantQuotesSearch/InstantQuotesSearchMainForm';
import PackagesPanel from '../../components/InstantQuotesSearch/PackagesPanel';
import ResultsPanel from '../../components/InstantQuotesSearch/ResultsPanel';

// ------------------------------------------
// Helpers
// ------------------------------------------

// pickFromSession: read a keyed value from a tab snapshot array
const pickFromSession = (arr, key) => (arr || []).find(x => x.key === key)?.value;

// toMidnightUTC: normalize "YYYY-MM-DD" or "YYYY-MM-DDTHH:mm:ssZ" into "YYYY-MM-DDT00:00:00Z"
const toMidnightUTC = (s) => {
  if (!s) return '';
  const dateOnly = String(s).includes('T') ? String(s).split('T')[0] : String(s);
  return `${dateOnly}T00:00:00Z`;
};

// Default schema used when OPTIONS fails or returns empty
const FALLBACK_SCHEMA = [
  { Name: 'OriginPlace', NormalizedName: 'Origin', Type: 'int', IsRequired: true, Order: 10 },
  { Name: 'DestinationPlace', NormalizedName: 'Destination', Type: 'int', IsRequired: false, Order: 20 },
  { Name: 'DepartureDate', NormalizedName: 'Departure', Type: 'date', IsRequired: true, Order: 30 },
  { Name: 'ContainerType', NormalizedName: 'Container', Type: 'int', IsRequired: true, Order: 40 },
  { Name: 'MarkupPercent', NormalizedName: 'Markup %', Type: 'string', IsRequired: false, Order: 50 },
];

//====================================================
// React component for the per-tab Instant Quotes flow
//====================================================
export default function InstantQuotesSearchConfigCrud({ handleTabClose, toastMessages }) {
  const { t } = useTranslation();
  const ctx = useContext(LoadingContext);
  const { renameTab } = useContext(NavigateContext);
  const dispatch = useDispatch();
  const M_State = useSelector((state) => state.StateKeyValue);

  // ---------------- Per-tab identity (tab id) ----------------
  const selectedTab = M_State.find(o => o.key === 'selectedTab')?.value;
  const [id, setId] = useState(selectedTab); // holds the current tab id for this instance

  // Seed values from global store (read once on boot, then cleared)
  const globalOperation = M_State.find(o => o.key === 'operation')?.value;                 // 'Create' | 'Edit' | 'View'
  const globalLine = M_State.find(o => o.key === 'InstantQuoteSearchLine')?.value;        // { Id, ... }

  // Local, per-tab snapshot of operation & selected line
  const [operation, setOperation] = useState(globalOperation);
  const [sessionLine, setSessionLine] = useState(globalLine || null);

  const isViewMode = (operation || '').toLowerCase() === 'view';

  // Core state kept per tab
  const [schema, setSchema] = useState([]);
  const [formInfo, setFormInfo] = useState({
    OriginPlaceId: null,
    DestinationPlaceId: null,
    DepartureDate: '',
    ContainerTypeId: 2, // default FCL
    MarkupPercent: '',
    OriginPlaceLabel: '',
    DestinationPlaceLabel: '',
    ServiceScope: 'P2P', // default scope
  });

  // Dictionaries
  const [containerTypes, setContainerTypes] = useState([]);
  const [packageTypes, setPackageTypes] = useState([]);

  // Packages
  const [fclItems, setFclItems] = useState([]);           // [{cargofiveEquipmentId, quantity, weightKg, displayIso?}]
  const [lclMode, setLclMode] = useState('volumes');      // 'volumes' | 'dimensions'
  const [lclBase, setLclBase] = useState('plt');          // 'plt' | 'box'
  const [lclvolumes, setLclvolumes] = useState({ quantity: '', volumeM3: '', weightKg: '' });
  const [lclDimensions, setLclDimensions] = useState([]); // [{quantity,hCm,wCm,lCm,weightKg}]

  // Results panel
  const [isResultsVisible, setIsResultsVisible] = useState(false);
  const [results, setResults] = useState([]);
  const [hasResultsOnce, setHasResultsOnce] = useState(false);

  // ------------------------------------------
  // Services (memoized per base URL)
  // ------------------------------------------
  const svcOptions = useMemo(() => new BufferService('CargofiveRateQueries', ctx.DEFAULTURL), [ctx.DEFAULTURL]);
  const svcContainerTypes = useMemo(() => new BufferService('ContainerTypes', ctx.DEFAULTURL), [ctx.DEFAULTURL]);
  const svcPackageTypes = useMemo(() => new BufferService('PackageTypes', ctx.DEFAULTURL), [ctx.DEFAULTURL]);
  const svcSearch = useMemo(() => new BufferService('CargofiveRateQueries/search', ctx.DEFAULTURL), [ctx.DEFAULTURL]);
  const svcQueries = useMemo(() => new BufferService('CargofiveRateQueries', ctx.DEFAULTURL), [ctx.DEFAULTURL]); // GET by Id (View)

  //====================================================
  // Update tab title using this tab's operation & line
  //====================================================
  useEffect(() => {
    if (!id) return;
    const base = operation === 'View'
      ? (t('LabelViewInstantQuote') || 'View Instant Quote')
      : (t('Instant Quotes Search') || 'Instant Quotes Search');
    const label = operation === 'View' && sessionLine?.Id ? `${base} #${sessionLine.Id}` : base;
    renameTab(String(id), label);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, operation, sessionLine?.Id, t]);

  //====================================================
  // Boot: restore this tab snapshot; if none, seed from
  // temporary globals once and clear those globals
  //====================================================
  useEffect(() => {
    const snap = M_State.find(o => o.key === id)?.value || [];

    // attempt to restore everything from the snapshot
    const sForm = pickFromSession(snap, 'formInfo');
    const sSchema = pickFromSession(snap, 'schema');
    const sCont = pickFromSession(snap, 'containerTypes');
    const sPack = pickFromSession(snap, 'packageTypes');

    const sFcl = pickFromSession(snap, 'fclItems');
    const sLclM = pickFromSession(snap, 'lclMode');
    const sLclB = pickFromSession(snap, 'lclBase');
    const sLclT = pickFromSession(snap, 'lclvolumes');
    const sLclD = pickFromSession(snap, 'lclDimensions');

    const sVis = pickFromSession(snap, 'isResultsVisible');
    const sRes = pickFromSession(snap, 'results');

    const sOp = pickFromSession(snap, 'operation');
    const sLine = pickFromSession(snap, 'sessionLine');
    const sHasOnce = pickFromSession(snap, 'hasResultsOnce');

    // If the snapshot had operation/line, prefer those (per tab isolation)
    if (sOp) setOperation(sOp);
    if (sLine) setSessionLine(sLine);

    // Restore the rest
    if (sForm) setFormInfo(sForm);
    if (typeof sHasOnce === 'boolean') setHasResultsOnce(sHasOnce);
    if (Array.isArray(sSchema)) setSchema(sSchema);
    if (Array.isArray(sCont)) setContainerTypes(sCont);
    if (Array.isArray(sPack)) setPackageTypes(sPack);

    if (Array.isArray(sFcl)) setFclItems(sFcl);
    if (sLclM) setLclMode(sLclM);
    if (sLclB) setLclBase(sLclB);
    if (sLclT) setLclvolumes(sLclT);
    if (Array.isArray(sLclD)) setLclDimensions(sLclD);

    if (typeof sVis === 'boolean') setIsResultsVisible(sVis);
    if (Array.isArray(sRes)) setResults(sRes);

    // If no snapshot existed, seed from globals once and then wipe globals
    if (!sOp && globalOperation !== undefined) {
      setOperation(globalOperation);
      dispatch(M_StateActions.removeM_State({ key: 'operation' })); // clear seed
    }
    if (!sLine && globalLine !== undefined) {
      setSessionLine(globalLine);
      dispatch(M_StateActions.removeM_State({ key: 'InstantQuoteSearchLine' })); // clear seed
    }

    // Fetch base lists when missing; if in View, hydrate entity for this tab
    (async () => {
      await fetchBaseIfNeeded({
        hasSchema: Array.isArray(sSchema) && sSchema.length > 0,
        hasContainerTypes: Array.isArray(sCont) && sCont.length > 0,
        hasPackageTypes: Array.isArray(sPack) && sPack.length > 0,
      });
      if ((sOp || globalOperation) === 'View' && (sLine?.Id || globalLine?.Id)) {
        await hydrateView(sLine?.Id || globalLine?.Id);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //====================================================
  // Persist per-tab snapshot (includes operation/line)
  //====================================================
  const persistTimer = useRef(null);
  useEffect(() => {
    if (!id) return;
    if (persistTimer.current) clearTimeout(persistTimer.current);

    // debounce snapshot writes a bit
    persistTimer.current = setTimeout(() => {
      const data = [
        { key: 'operation', value: operation },
        { key: 'sessionLine', value: sessionLine },
        { key: 'formInfo', value: formInfo },
        { key: 'hasResultsOnce', value: hasResultsOnce },
        { key: 'schema', value: schema },
        { key: 'containerTypes', value: containerTypes },
        { key: 'packageTypes', value: packageTypes },
        { key: 'fclItems', value: fclItems },
        { key: 'lclMode', value: lclMode },
        { key: 'lclBase', value: lclBase },
        { key: 'lclvolumes', value: lclvolumes },
        { key: 'lclDimensions', value: lclDimensions },
        { key: 'isResultsVisible', value: isResultsVisible },
        { key: 'results', value: results },
      ];
      dispatch(M_StateActions.addM_State({ key: id, value: data }));
    }, 200);

    return () => { if (persistTimer.current) clearTimeout(persistTimer.current); };
  }, [
    id,
    operation,
    sessionLine,
    formInfo,
    hasResultsOnce,
    schema,
    containerTypes,
    packageTypes,
    fclItems,
    lclMode,
    lclBase,
    lclvolumes,
    lclDimensions,
    isResultsVisible,
    results,
    dispatch
  ]);

  //====================================================
  // When the selected tab changes, restore that tab's
  // snapshot only (do not read global seeds again)
  //====================================================
  useEffect(() => {
    const newId = M_State.find(o => o.key === 'selectedTab')?.value;
    if (newId && newId !== id) {
      setId(newId);
      const data = M_State.find(o => o.key === newId)?.value;
      if (!data) return;

      const pick = (k) => (data || []).find((x) => x.key === k)?.value;

      setOperation(pick('operation') || 'View');
      setSessionLine(pick('sessionLine') || null);
      setHasResultsOnce(!!pick('hasResultsOnce'));

      setFormInfo(pick('formInfo') || {
        OriginPlaceId: null,
        DestinationPlaceId: null,
        DepartureDate: '',
        ContainerTypeId: 2,
        MarkupPercent: '',
        OriginPlaceLabel: '',
        DestinationPlaceLabel: '',
        ServiceScope: 'P2P',
      });

      setSchema(pick('schema') || []);
      setContainerTypes(pick('containerTypes') || []);
      setPackageTypes(pick('packageTypes') || []);
      setFclItems(pick('fclItems') || []);
      setLclMode(pick('lclMode') || 'volumes');
      setLclBase(pick('lclBase') || 'plt');
      setLclvolumes(pick('lclvolumes') || { quantity: '', volumeM3: '', weightKg: '' });
      setLclDimensions(pick('lclDimensions') || []);
      setIsResultsVisible(!!pick('isResultsVisible'));
      setResults(pick('results') || []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [M_State]);

  //====================================================
  // Fetch OPTIONS + dictionaries when missing
  //====================================================
  const fetchBaseIfNeeded = async ({
    hasSchema,
    hasContainerTypes,
    hasPackageTypes,
  }) => {
    ctx.setIsLoad(true);
    try {
      // OPTIONS
      if (!hasSchema) {
        const opts = await svcOptions.options();
        if (isFailurePayload(opts) || !Array.isArray(opts) || opts.length === 0) {
          setSchema(FALLBACK_SCHEMA);
        } else {
          // keep only the fields we use and sort
          const wanted = new Set(['OriginPlace', 'DestinationPlace', 'DepartureDate', 'ContainerType', 'MarkupPercent']);
          const filtered = (opts || []).filter(o => wanted.has(o?.Name));
          filtered.sort((a, b) => {
            const ao = a?.Order ?? 0; const bo = b?.Order ?? 0;
            if (ao !== bo) return ao - bo;
            return String(a?.Name || '').localeCompare(String(b?.Name || ''));
          });
          setSchema(filtered.length ? filtered : FALLBACK_SCHEMA);
        }
      }

      // ContainerTypes
      if (!hasContainerTypes) {
        const ct = await svcContainerTypes.get();
        if (!isFailurePayload(ct)) setContainerTypes(ct || []);
      }

      // PackageTypes
      if (!hasPackageTypes) {
        const pt = await svcPackageTypes.get();
        if (!isFailurePayload(pt)) setPackageTypes(pt || []);
      }
    } catch (err) {
      toastMessages('error', '', apiMessage(err, t('ErrorLoadingData')));
    } finally {
      ctx.setIsLoad(false);
    }
  };

  //====================================================
  // Hydrate View: fetch entity by Id and map into form
  //====================================================
  const hydrateView = async (queryId) => {
    ctx.setIsLoad(true);
    try {
      const det = await svcQueries.getId(queryId);
      if (isFailurePayload(det)) throw det;

      // map header fields
      setFormInfo({
        OriginPlaceId: det.OriginPlaceId,
        DestinationPlaceId: det.DestinationPlaceId,
        DepartureDate: det.DepartureDate?.slice(0, 10) || '',
        ContainerTypeId: det.ContainerTypeId, // 1=LCL 2=FCL
        MarkupPercent: det.MarkupPercent || '',
        OriginPlaceLabel: det.OriginPlace,
        DestinationPlaceLabel: det.DestinationPlace,
        ServiceScope: 'P2P',
      });

      // map cargo details
      const cargo = String(det.CargoDetails || '');
      if (Number(det.ContainerTypeId) === 2) {
        const rows = cargo
          .split(',')
          .map(s => s.trim())
          .filter(Boolean)
          .map(tok => {
            const m = tok.match(/^(\d+)x([0-9A-Z]+)x(\d+)$/i);
            if (!m) return null;
            const [, qty, iso, kg] = m;
            return {
              // Dont resolve equipmentId here; user can edit if needed
              // In view mode, we dont need to resolve cargofiveEquipmentId; just display ISO
              cargofiveEquipmentId: null, 
              displayIso: iso,
              quantity: Number(qty),
              weightKg: Number(kg),
            };
          })
          .filter(Boolean);

        setFclItems(rows);
      } else {
        // LCL can be "volumes" or "dimensions"
        if (cargo.includes('cm')) {
          // dimensions: "{base}x{QTD}x{H}x{W}x{L}xcmx{PESO}xkg"
          setLclMode('dimensions');
          const first = cargo.split(',')[0] || '';
          setLclBase(first.startsWith('box') ? 'box' : 'plt');
          const dims = cargo.split(',')
            .map(s => s.trim())
            .filter(Boolean)
            .map(tok => {
              const m = tok.match(/^(?:plt|box)x(\d+)x(\d+)x(\d+)x(\d+)xcmx(\d+)xkg$/i);
              if (!m) return null;
              const [, q, h, w, l, kg] = m;
              return { quantity: Number(q), hCm: Number(h), wCm: Number(w), lCm: Number(l), weightKg: Number(kg) };
            })
            .filter(Boolean);
          setLclDimensions(dims);
        } else {
          // volumes: "{base}x{QTD}x{VOL}xm3x{PESO}xkg"
          setLclMode('volumes');
          const m = cargo.match(/^(plt|box)x(\d+)x([0-9.]+)xm3x(\d+)xkg$/i);
          if (m) {
            const [, base, q, v, kg] = m;
            setLclBase(base.toLowerCase() === 'box' ? 'box' : 'plt');
            setLclvolumes({ quantity: Number(q), volumeM3: String(v), weightKg: Number(kg) });
          }
        }
      }

      setIsResultsVisible(false); // ensure results are hidden after hydration
    } catch (err) {
      toastMessages('error', '', apiMessage(err, t('ErrorLoadingData')));
    } finally {
      ctx.setIsLoad(false);
    }
  };

  //====================================================
  // Run search using current tab's state (Create flow)
  //====================================================
  const onSearch = useCallback(async () => {
    const originId = formInfo?.OriginPlaceId;
    const destId = formInfo?.DestinationPlaceId;
    const date = formInfo?.DepartureDate;
    const contId = formInfo?.ContainerTypeId;

    // basic validation
    if (!originId || !date || !contId) {
      toastMessages('error', '', t('MandatoryFields') || 'Please fill the mandatory fields.');
      return;
    }
    if (originId && destId && Number(originId) === Number(destId)) {
      toastMessages('error', '', t('OriginAndDestinationMustDiffer') || 'Origin and Destination must be different.');
      return;
    }

    // parse raw percentage the user typed; accepts "10" or "10%"
    const toRawPercent = (val) => {
      if (val == null) return 0;
      const s = String(val).trim();
      if (s.endsWith('%')) {
        const n = Number(s.replace('%', '').trim());
        return Number.isFinite(n) ? n : 0;
      }
      const n = Number(s);
      return Number.isFinite(n) ? n : 0;
    };

    // payload base
    const payload = {
      originPlaceId: Number(originId),
      destinationPlaceId: destId != null ? Number(destId) : 0,
      departureDate: toMidnightUTC(formInfo?.DepartureDate), // normalized to UTC midnight
      containerTypeId: Number(contId),
      includeOriginCharges: true,
      includeDestinationCharges: true,
      includeImoCharges: false,
      markupPercent: toRawPercent(formInfo?.MarkupPercent) || 0,
      fclItems: [],
      lclCargoDetails: null,
      lclDimensions: null,
    };

    if (Number(contId) === 2) {
      // FCL requires at least one line
      if ((fclItems || []).length === 0) {
        toastMessages('error', '', t('AddAtLeastOneContainer') || 'Add at least one FCL line.');
        return;
      }
      // map FCL lines
      payload.fclItems = fclItems.map(x => ({
        cargofiveEquipmentId: Number(x?.cargofiveEquipmentId),
        quantity: Number(x?.quantity || 0),
        weightKg: Number(x?.weightKg || 0),
      }));
    } else {
      // LCL: either totals ("volumes") or dimensional lines ("dimensions")
      const prefix = lclBase === 'box' || lclBase === 'plt' ? lclBase : 'plt';

      if (lclMode === 'volumes') {
        const q = Number(lclvolumes?.quantity || 0);
        const v = String(lclvolumes?.volumeM3 || '').trim();
        const w = Number(lclvolumes?.weightKg || 0);
        if (!q || !v || !w) {
          toastMessages('error', '', t('FillLclvolumes') || 'Fill LCL volumes: quantity, volume (m3), weight (kg).');
          return;
        }
        payload.lclCargoDetails = `${prefix}x${q}x${v}xm3x${w}xkg`;
      } else {
        if (!Array.isArray(lclDimensions) || lclDimensions.length === 0) {
          toastMessages('error', '', t('AddAtLeastOneDimensionLine') || 'Add at least one LCL dimension line.');
          return;
        }
        const lines = lclDimensions.map(d => {
          const q = Number(d?.quantity || 0);
          const h = Number(d?.hCm || 0);
          const w = Number(d?.wCm || 0);
          const l = Number(d?.lCm || 0);
          const kg = Number(d?.weightKg || 0);
          return `${prefix}x${q}x${h}x${w}x${l}xcmx${kg}xkg`;
        });
        payload.lclDimensions = lines.join(',');
      }
    }

    // call search
    ctx.setIsLoad(true);
    try {
      const res = await svcSearch.post(payload);
      if (isFailurePayload(res)) {
        toastMessages('error', '', apiMessage(res, t('ErrorLoadingData')));
        return;
      }

      // --- helpers locais ---
      const pascalToCamel = (s) => s ? s[0].toLowerCase() + s.slice(1) : s;

      // normalizes all object keys recursively from PascalCase to camelCase
      const normalize = (node) => {
        if (Array.isArray(node)) return node.map(normalize);
        if (node && typeof node === 'object') {
          const out = {};
          for (const k of Object.keys(node)) {
            const v = node[k];
            const ck = pascalToCamel(k);
            out[ck] = normalize(v);
          }
          return out;
        }
        return node;
      };

      const shapeToOffers = (r) => {
        if (!r) return [];
        if (Array.isArray(r)) return r;
        if (Array.isArray(r.Offers)) return r.Offers;   
        if (Array.isArray(r.Items)) return r.Items;
        return [];
      };

      const offersRaw = shapeToOffers(res);
      // normalize each offer and keep the original as __raw
      const items = offersRaw.map(o => ({ ...normalize(o), __raw: o }));

      // sort by totalFinalUsd, transitDays, departureUtc
      const toNum = (v) => Number(v ?? 0);
      items.sort((a, b) => {
        const ta = toNum(a.totalFinalUsd);
        const tb = toNum(b.totalFinalUsd);
        if (ta !== tb) return ta - tb;
        const tta = toNum(a.transitDays);
        const ttb = toNum(b.transitDays);
        if (tta !== ttb) return tta - ttb;
        return String(a.departureUtc || '').localeCompare(String(b.departureUtc || ''));
      });

      setResults(items);
      setHasResultsOnce(true);
      setIsResultsVisible(true);
    } catch (err) {
      toastMessages('error', '', apiMessage(err, t('ErrorLoadingData')));
    } finally {
      ctx.setIsLoad(false);
    }
  }, [
    ctx,
    t,
    toastMessages,
    formInfo,
    fclItems,
    lclMode,
    lclBase,
    lclvolumes,
    lclDimensions,
    svcSearch
  ]);

  //====================================================
  // UI (header actions + outer card + children panels)
  //====================================================
  return (
    <>
      <MHeader
        element={
          <>
            <MButton
              tooltip={t('RefreshTable')}
              icon={<i className="pi pi-refresh iconTable"></i>}
              onClick={() => fetchBaseIfNeeded({
                hasSchema: schema.length > 0,
                hasContainerTypes: containerTypes.length > 0,
                hasPackageTypes: packageTypes.length > 0
              })}
              label={t('RefreshTable')}
            />
            {!isViewMode && (
              <MButton
                tooltip={t('Search')}
                icon={<i className="pi pi-search iconTable"></i>}
                onClick={onSearch}
                label={t('Search')}
              />

            )}
            {!isViewMode && hasResultsOnce && (
              <MButton
                tooltip={isResultsVisible ? t('Hide Results') : t('Show Results')}
                icon={<i className={`pi ${isResultsVisible ? 'pi-eye-slash' : 'pi-eye'} iconTable`} />}
                onClick={() => setIsResultsVisible(v => !v)}
                label={isResultsVisible ? t('Hide Results') : t('Show Results')}
              />
            )}

          </>
        }
      />

      <div  style={{
            marginBottom: '10px',
            padding: '20px',
          }} >
        {/* Single outer card keeps the whole flow visually grouped */}
        <div className="firstDivContainer"
        >
          <InstantQuotesSearchMainForm
            schema={schema}
            formInfo={formInfo}
            setFormInfo={isViewMode ? () => { } : setFormInfo} // disable editing in View
            containerTypes={containerTypes}
            packageTypes={packageTypes}
            isViewMode={isViewMode}
          />

          <PackagesPanel
            containerTypeId={Number(formInfo?.ContainerTypeId || 2)} // 1=LCL, 2=FCL
            fclItems={fclItems}
            setFclItems={isViewMode ? () => { } : setFclItems}
            lclMode={lclMode}
            setLclMode={isViewMode ? () => { } : setLclMode}
            lclBase={lclBase}
            setLclBase={isViewMode ? () => { } : setLclBase}
            packageTypes={packageTypes}
            lclvolumes={lclvolumes}
            setLclvolumes={isViewMode ? () => { } : setLclvolumes}
            lclDimensions={lclDimensions}
            setLclDimensions={isViewMode ? () => { } : setLclDimensions}
            readOnly={isViewMode}
          />

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <MButton
              icon={<i className="pi pi-search" style={{ color: 'var(--color-white)' }} />}
              label={t('Search')}
              backgroundColor={'var(--color-red)'}
              labelColor={'var(--color-white)'}
              onClick={onSearch}
              disabled={isViewMode}
              size="large"
              style={{ marginTop: '10px', minWidth: '150px' }}
              fillMode="outline"
              normal 
              hoverEffect
            />
          </div>
        </div>

        {/* Results Panel */}
        <ResultsPanel
          visible={isResultsVisible}
          onClose={() => setIsResultsVisible(false)}
          results={results}
          t={t}
          toastMessages={toastMessages}
        />
      </div>
    </>
  );
}

InstantQuotesSearchConfigCrud.propTypes = {
  handleTabClose: PropTypes.func,
  toastMessages: PropTypes.func,
};
